import { useMemo } from 'react';
import {
    CartesianGrid,
    Line,
    LineChart,
    ResponsiveContainer,
    Tooltip,
    XAxis,
    YAxis,
} from 'recharts';
import { INSIGHTS } from '@/constants/testIds';

const SERIES_META = {
    weight: { color: '#00FF9D', label: 'Top set' },
    e1rm: { color: '#A78BFA', label: 'e1RM' },
    volume: { color: '#FF4D4D', label: 'Volume' },
};

function fmtDate(iso) {
    const d = new Date(iso);
    return d.toLocaleDateString(undefined, { month: 'short', day: 'numeric' });
}

function ChartTooltip({ active, payload, units, visible }) {
    if (!active || !payload?.length) return null;
    const row = payload[0].payload;
    return (
        <div className="glass-card rounded-xl px-3 py-2 min-w-[160px]">
            <p className="uppercase-mono text-muted-foreground mb-1">
                {fmtDate(row.date)}
            </p>
            <div className="space-y-0.5">
                {visible.weight && (
                    <Line2 color={SERIES_META.weight.color} label="Top set" value={`${row.weight} ${units}`} />
                )}
                {visible.e1rm && (
                    <Line2 color={SERIES_META.e1rm.color} label="e1RM" value={`${row.e1rm.toFixed(1)} ${units}`} />
                )}
                {visible.volume && (
                    <Line2 color={SERIES_META.volume.color} label="Volume" value={`${Math.round(row.volume).toLocaleString()} ${units}`} />
                )}
            </div>
        </div>
    );
}
function Line2({ color, label, value }) {
    return (
        <div className="flex items-center justify-between gap-3">
            <span className="inline-flex items-center gap-2">
                <span
                    className="w-2 h-2 rounded-full"
                    style={{ background: color }}
                />
                <span className="uppercase-mono text-muted-foreground">
                    {label}
                </span>
            </span>
            <span className="font-mono text-xs font-bold tabular-nums">
                {value}
            </span>
        </div>
    );
}

export function ExerciseProgressionChart({
    points = [],
    units = 'kg',
    visible = { weight: true, e1rm: true, volume: false },
    isDark = true,
}) {
    const data = useMemo(
        () =>
            points.map((p) => ({
                date: p.date,
                weight: p.top_weight,
                e1rm: p.best_e1rm,
                volume: p.volume,
            })),
        [points]
    );

    if (!data.length) {
        return (
            <div
                data-testid={INSIGHTS.progressionEmpty}
                className="glass-card rounded-2xl p-8 text-center"
            >
                <p className="font-display font-bold text-lg">
                    No completed sets yet for this exercise.
                </p>
                <p className="text-sm text-muted-foreground mt-1">
                    Log a working set in a session to start the curve.
                </p>
            </div>
        );
    }

    const gridColor = isDark ? 'rgba(255,255,255,0.05)' : 'rgba(0,0,0,0.05)';
    const tickFill = isDark ? '#A0A0A0' : '#666666';
    // Volume can dwarf weight numerically, so push it to a secondary right axis
    // when both visible.
    const showRightAxis = visible.volume && (visible.weight || visible.e1rm);

    return (
        <div
            data-testid={INSIGHTS.progressionChart}
            className="w-full h-[300px] sm:h-[340px]"
        >
            <ResponsiveContainer width="100%" height="100%" minWidth={0} minHeight={0}>
                <LineChart data={data} margin={{ top: 12, right: showRightAxis ? 24 : 12, left: 0, bottom: 4 }}>
                    <CartesianGrid
                        strokeDasharray="2 4"
                        stroke={gridColor}
                        vertical={false}
                    />
                    <XAxis
                        dataKey="date"
                        tickFormatter={fmtDate}
                        stroke={tickFill}
                        tick={{ fill: tickFill, fontSize: 11, fontFamily: 'JetBrains Mono' }}
                        tickLine={false}
                        axisLine={false}
                        minTickGap={24}
                    />
                    <YAxis
                        yAxisId="left"
                        stroke={tickFill}
                        tick={{ fill: tickFill, fontSize: 11, fontFamily: 'JetBrains Mono' }}
                        tickLine={false}
                        axisLine={false}
                        width={40}
                    />
                    {showRightAxis && (
                        <YAxis
                            yAxisId="right"
                            orientation="right"
                            stroke={SERIES_META.volume.color}
                            tick={{ fill: SERIES_META.volume.color, fontSize: 11, fontFamily: 'JetBrains Mono' }}
                            tickLine={false}
                            axisLine={false}
                            width={50}
                        />
                    )}
                    <Tooltip
                        cursor={{ stroke: gridColor }}
                        content={<ChartTooltip units={units} visible={visible} />}
                    />
                    {visible.weight && (
                        <Line
                            type="monotone"
                            dataKey="weight"
                            yAxisId="left"
                            stroke={SERIES_META.weight.color}
                            strokeWidth={2.5}
                            dot={{ r: 3, fill: SERIES_META.weight.color, strokeWidth: 0 }}
                            activeDot={{ r: 5 }}
                            isAnimationActive={false}
                        />
                    )}
                    {visible.e1rm && (
                        <Line
                            type="monotone"
                            dataKey="e1rm"
                            yAxisId="left"
                            stroke={SERIES_META.e1rm.color}
                            strokeWidth={2}
                            strokeDasharray="4 4"
                            dot={{ r: 2.5, fill: SERIES_META.e1rm.color, strokeWidth: 0 }}
                            isAnimationActive={false}
                        />
                    )}
                    {visible.volume && (
                        <Line
                            type="monotone"
                            dataKey="volume"
                            yAxisId={showRightAxis ? 'right' : 'left'}
                            stroke={SERIES_META.volume.color}
                            strokeWidth={2}
                            dot={{ r: 2.5, fill: SERIES_META.volume.color, strokeWidth: 0 }}
                            isAnimationActive={false}
                        />
                    )}
                </LineChart>
            </ResponsiveContainer>
        </div>
    );
}
